<template>
  <div
    class="fc-root"
    :class="{ 'is-dragging': dragging, 'is-static': !interactive }"
    :style="rootStyle"
    role="group"
    aria-roledescription="carousel"
    :aria-label="ariaLabel"
    :aria-hidden="interactive ? null : 'true'"
    :tabindex="interactive && canRun ? 0 : -1"
    @keydown="onKeydown"
    @focusin="onFocusIn"
    @focusout="onFocusOut"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
  >
    <div class="fc-viewport" :style="viewportStyle">
      <div ref="stage" class="fc-stage" :style="stageStyle">
        <div
          v-for="(item, i) in renderItems"
          :key="i"
          class="fc-card"
          :class="{ 'fc-card--framed': frame }"
          :style="cardStyle"
          @click="onCardClick(i, item, $event)"
        >
          <img
            class="fc-card__img"
            :src="item.src"
            :alt="item.alt"
            draggable="false"
            decoding="async"
          />
          <div v-if="item.title || item.subtitle" class="fc-card__meta">
            <p v-if="item.title" class="fc-card__title">{{ item.title }}</p>
            <p v-if="item.subtitle" class="fc-card__subtitle">
              {{ item.subtitle }}
            </p>
          </div>
        </div>
      </div>

      <div v-if="showDots && items.length > 1" ref="dots" class="fc-dots">
        <button
          v-for="(item, i) in items"
          :key="i"
          type="button"
          class="fc-dot"
          :disabled="!interactive"
          :tabindex="interactive ? 0 : -1"
          :style="dotStyle(i)"
          :aria-label="dotLabel(i)"
          :aria-current="i === activeIndex ? 'true' : 'false'"
          @click="goTo(i)"
        ></button>
      </div>
    </div>

    <!-- 自动播放时不播报，否则每 2.6s 打断一次读屏 -->
    <p class="fc-live" :aria-live="playing ? 'off' : 'polite'">
      {{ liveText }}
    </p>
  </div>
</template>

<script>
import {
  STAGE,
  PERSPECTIVE,
  ORIGIN,
  CARD,
  DOTS,
  TIMING,
  slotAt,
  zoomAt,
  cubicBezier,
  EASE
} from "./geometry";

/** 环形至少需要这么多张卡片，否则回绕点会落在仍然可见的槽位上 */
const MIN_RING = 8;
/** 拖拽多少设计像素算作切换一张 */
const DRAG_STEP = 268;
/** 超过该位移判定为拖拽而非点击 */
const DRAG_SLOP = 5;
/** 拖拽结束后这段时间内的 click 一律忽略（触摸端 click 会延迟派发） */
const CLICK_GUARD = 400;

export default {
  name: "FlowCarousel",

  props: {
    /** 卡片列表，元素可以是图片地址，也可以是 { src, title, subtitle, alt } */
    items: {
      type: Array,
      default: () => []
    },
    /** 相邻两次自动切换的间隔（含位移时间） */
    interval: {
      type: Number,
      default: TIMING.interval
    },
    /** 单次位移时长 */
    duration: {
      type: Number,
      default: TIMING.duration
    },
    autoplay: {
      type: Boolean,
      default: true
    },
    /** 鼠标悬停时暂停自动播放（仅在真正有指针悬停能力的设备上生效） */
    pauseOnHover: {
      type: Boolean,
      default: true
    },
    draggable: {
      type: Boolean,
      default: true
    },
    showDots: {
      type: Boolean,
      default: true
    },
    /** 是否给卡片加金色外框（参考动画中的样式） */
    frame: {
      type: Boolean,
      default: true
    },
    /** 中心卡在停留期内缓慢放大（参考动画里的"呼吸"效果） */
    breathe: {
      type: Boolean,
      default: true
    },
    /**
     * 是否接受交互。落地页里当纯装饰层用时传 false：
     * 不吃指针事件、不进 Tab 序、对读屏隐藏，只留自动播放。
     */
    interactive: {
      type: Boolean,
      default: true
    },
    /** 舞台最大缩放比，1 表示最大按设计稿 1200px 宽呈现 */
    maxScale: {
      type: Number,
      default: 1
    },
    /** 自定义缓动，形如 [x1, y1, x2, y2] */
    easing: {
      type: Array,
      default: null
    },
    ariaLabel: {
      type: String,
      default: "图片轮播"
    }
  },

  data() {
    return {
      scale: 1,
      dragging: false,
      activeIndex: 0,
      playing: false
    };
  },

  computed: {
    /**
     * 参与渲染的卡片。数量不足 MIN_RING 时整体复制若干轮，
     * 让环形回绕点始终落在完全透明的槽位上（见 geometry.js）。
     * 这里不做过滤，保证下标和 items 一一对应（指示点、goTo 都按 items 下标走）。
     */
    renderItems() {
      const list = this.items.map(normalizeItem);
      if (list.length < 2) return list;
      const copies = Math.ceil(MIN_RING / list.length);
      const out = [];
      for (let c = 0; c < copies; c++) out.push(...list);
      return out;
    },
    rootStyle() {
      return { height: Math.round(STAGE.H * this.scale) + "px" };
    },
    viewportStyle() {
      return {
        width: STAGE.W + "px",
        height: STAGE.H + "px",
        transform: "scale(" + this.scale + ")",
        marginLeft: -STAGE.W / 2 + "px"
      };
    },
    stageStyle() {
      return {
        perspective: PERSPECTIVE + "px",
        perspectiveOrigin: ORIGIN.X + "px " + ORIGIN.Y + "px"
      };
    },
    cardStyle() {
      return {
        width: CARD.W + "px",
        height: CARD.H + "px",
        left: CARD.X + "px",
        top: CARD.Y + "px",
        borderRadius: CARD.RADIUS + "px"
      };
    },
    canRun() {
      return this.renderItems.length > 1;
    },
    liveText() {
      const n = this.items.length;
      return n ? "第 " + (this.activeIndex + 1) + " / " + n + " 张" : "";
    }
  },

  watch: {
    items() {
      this.clearTimer();
      this.cancelAnim();
      this.position = 0;
      this.activeIndex = 0;
      this.$nextTick(this.schedule);
    },
    autoplay(v) {
      if (v) this.schedule();
      else this.pause();
    },
    pauseOnHover(v) {
      if (!v) {
        this.hovering = false;
        this.schedule();
      }
    },
    draggable(v) {
      if (v) this.bindDrag();
      else this.unbindDrag();
    },
    breathe() {
      this.render();
      this.requestFrame();
    },
    easing: {
      immediate: true,
      handler(v) {
        this.ease =
          Array.isArray(v) && v.length === 4 ? cubicBezier(...v) : EASE;
      }
    }
  },

  created() {
    // 动画状态刻意放在响应式数据之外：每帧都会变化，走 Vue 的更新队列没有意义
    this.position = 0;
    this.raf = 0;
    this.timer = 0;
    this.lastStart = 0;
    this.anim = null;
    this.drag = null;
    this.dragEndAt = 0;
    this.hovering = false;
    this.focused = false;
    this.visible = true;
    this.hoverCapable = false;
    this.reduceMotion = false;
    this.io = null;
    this.ro = null;
    this.motionMq = null;
    this.written = []; // 每个卡片上一次写入的样式，避免重复写
  },

  mounted() {
    this.hoverCapable = matches("(hover: hover) and (pointer: fine)");
    this.watchReduceMotion();
    this.measure();
    this.observeResize();
    this.observeVisibility();
    this.lastStart = now();
    this.render();
    this.bindDrag();
    document.addEventListener("visibilitychange", this.onVisibilityChange);
    this.schedule();
  },

  /** 任何一次重渲染（增删卡片、切换指示点显示…）之后都要把样式补回去 */
  updated() {
    this.render();
  },

  beforeDestroy() {
    this.clearTimer();
    this.cancelAnim();
    this.unbindDrag();
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
    window.removeEventListener("resize", this.measure);
    if (this.motionMq) {
      if (this.motionMq.removeEventListener) {
        this.motionMq.removeEventListener("change", this.onMotionChange);
      } else if (this.motionMq.removeListener) {
        this.motionMq.removeListener(this.onMotionChange);
      }
    }
    if (this.ro) this.ro.disconnect();
    if (this.io) this.io.disconnect();
  },

  methods: {
    /* ---------- 环境 ---------- */

    measure() {
      const w = this.$el ? this.$el.clientWidth : 0;
      if (!w) return;
      this.scale = Math.min(w / STAGE.W, this.maxScale);
    },

    observeResize() {
      if (typeof ResizeObserver !== "undefined") {
        this.ro = new ResizeObserver(() => this.measure());
        this.ro.observe(this.$el);
      } else {
        window.addEventListener("resize", this.measure);
      }
    },

    /** 滚出视口时停掉动画，避免离屏空转 */
    observeVisibility() {
      if (typeof IntersectionObserver === "undefined") return;
      this.io = new IntersectionObserver(
        entries => {
          const on = entries[entries.length - 1].isIntersecting;
          if (on === this.visible) return;
          this.visible = on;
          if (on) this.schedule();
          else this.pause();
        },
        { threshold: 0 }
      );
      this.io.observe(this.$el);
    },

    /** 系统「减少动态效果」开关是可以随时改的，要跟着走 */
    watchReduceMotion() {
      if (!window.matchMedia) return;
      this.motionMq = window.matchMedia("(prefers-reduced-motion: reduce)");
      this.reduceMotion = this.motionMq.matches;
      this.onMotionChange = () => {
        this.reduceMotion = this.motionMq.matches;
        if (this.reduceMotion) this.pause();
        else this.schedule();
      };
      if (this.motionMq.addEventListener) {
        this.motionMq.addEventListener("change", this.onMotionChange);
      } else if (this.motionMq.addListener) {
        this.motionMq.addListener(this.onMotionChange);
      }
    },

    onVisibilityChange() {
      if (document.hidden) this.pause();
      else this.schedule();
    },

    onMouseEnter() {
      // 触摸端点一下也会派发 mouseenter，且不会再有 mouseleave，
      // 只在真正能悬停的设备上启用悬停暂停
      if (!this.interactive || !this.pauseOnHover || !this.hoverCapable) return;
      this.hovering = true;
      this.pause();
    },

    onMouseLeave() {
      if (!this.interactive || !this.pauseOnHover || !this.hoverCapable) return;
      this.hovering = false;
      this.schedule();
    },

    onFocusIn() {
      if (!this.interactive) return;
      this.focused = true;
      this.pause();
    },

    onFocusOut(ev) {
      if (ev.relatedTarget && this.$el.contains(ev.relatedTarget)) return;
      this.focused = false;
      this.schedule();
    },

    onKeydown(ev) {
      if (!this.interactive || !this.canRun) return;
      const n = this.items.length;
      switch (ev.key) {
        case "ArrowLeft":
        case "Left":
          this.prev();
          break;
        case "ArrowRight":
        case "Right":
          this.next();
          break;
        case "Home":
          this.goTo(0);
          break;
        case "End":
          this.goTo(n - 1);
          break;
        default:
          return;
      }
      ev.preventDefault();
    },

    /* ---------- 渲染 ---------- */

    /** 把 position 直接写进 DOM。每帧调用，不触发 Vue 更新 */
    render() {
      const stage = this.$refs.stage;
      if (!stage) return;
      const cards = stage.children;
      const n = cards.length;
      if (!n) return;

      const holdT = this.holdProgress();
      const zooming = this.breathe && !this.reduceMotion;

      for (let i = 0; i < n; i++) {
        const el = cards[i];
        const d = n > 1 ? wrap(i - this.position, n) : 0;
        const s = slotAt(d);
        const prev = this.written[i] || (this.written[i] = {});

        if (s.opacity <= 0.0015) {
          if (prev.hidden !== true) {
            prev.hidden = true;
            el.style.visibility = "hidden";
            el.style.opacity = "0";
          }
          continue;
        }
        if (prev.hidden !== false) {
          prev.hidden = false;
          el.style.visibility = "";
        }

        // scale 放在最右边，先在卡片自身平面里等比放大，再旋转、平移
        const zoom = zooming ? zoomAt(d, holdT) : 1;
        const transform =
          "translateX(" +
          s.translateX.toFixed(2) +
          "px) translateZ(" +
          s.translateZ.toFixed(2) +
          "px) rotateY(" +
          s.rotateY.toFixed(3) +
          "deg) scale(" +
          zoom.toFixed(5) +
          ")";
        if (transform !== prev.transform) {
          prev.transform = transform;
          el.style.transform = transform;
        }
        const opacity = s.opacity.toFixed(4);
        if (opacity !== prev.opacity) {
          prev.opacity = opacity;
          el.style.opacity = opacity;
        }
        // 量化成整数，位移过程中绝大多数帧都不需要重写层级
        const z = String(1000 - Math.round(Math.abs(d) * 10));
        if (z !== prev.z) {
          prev.z = z;
          el.style.zIndex = z;
        }
      }
      this.renderDots();
      this.syncActiveIndex();
    },

    renderDots() {
      const box = this.$refs.dots;
      if (!box) return;
      const dots = box.children;
      const n = this.items.length;
      if (!n) return;
      for (let i = 0; i < dots.length; i++) {
        const d = Math.abs(wrap(i - this.position, n));
        const k = Math.max(0, 1 - d); // 1 = 激活，0 = 普通
        const el = dots[i];
        const w =
          (DOTS.SIZE + (DOTS.ACTIVE_W - DOTS.SIZE) * k).toFixed(2) + "px";
        const c = mixRgb(DOTS.COLOR, DOTS.ACTIVE_COLOR, k);
        if (el.style.width !== w) el.style.width = w;
        if (el.style.backgroundColor !== c) el.style.backgroundColor = c;
      }
    },

    syncActiveIndex() {
      const n = this.items.length;
      if (!n) return;
      const idx = ((Math.round(this.position) % n) + n) % n;
      if (idx !== this.activeIndex) {
        this.activeIndex = idx;
        this.$emit("change", idx, this.items[idx]);
      }
    },

    dotStyle(i) {
      const n = this.items.length;
      const x = DOTS.CX + (i - (n - 1) / 2) * DOTS.PITCH;
      return {
        left: x + "px",
        top: DOTS.CY - DOTS.SIZE / 2 + "px",
        height: DOTS.SIZE + "px",
        borderRadius: DOTS.SIZE / 2 + "px"
      };
    },

    dotLabel(i) {
      const item = normalizeItem(this.items[i]);
      return item.title || item.alt || "第 " + (i + 1) + " 张";
    },

    /* ---------- 播放 ---------- */

    canAutoplay() {
      return (
        this.autoplay &&
        this.canRun &&
        !this.reduceMotion &&
        !this.hovering &&
        !this.focused &&
        !this.dragging &&
        this.visible &&
        !document.hidden &&
        this.interval > 0
      );
    },

    /**
     * 预约下一次自动位移。
     * 只动定时器、不打断正在进行的位移——否则悬停/离屏会把卡片
     * 停在小数槽位上，之后整条轨道都会错位。
     */
    schedule() {
      this.clearTimer();
      this.playing = this.canAutoplay();
      if (this.anim || !this.playing) return;
      // 以「上一次位移开始的时刻」计时，避免每轮多攒一个 rAF 的误差；
      // 暂停后已经错过时间点的，按一个完整停留时间重新起算，不要立刻蹦一下
      const rest = Math.max(0, this.interval - this.duration);
      const due = this.lastStart ? this.lastStart + this.interval - now() : 0;
      let hold = due;
      if (due <= 0) {
        // 已经错过时间点（刚从暂停恢复等），按一个完整停留期重新起算，
        // lastStart 一并回拨，缩放也跟着从头开始
        hold = rest;
        this.lastStart = now() - this.duration;
      }
      this.requestFrame();
      this.timer = setTimeout(() => {
        this.timer = 0;
        this.step(1);
      }, hold);
    },

    /** 暂停自动播放；正在进行的位移会照常走完并落到整数槽位 */
    pause() {
      this.clearTimer();
      this.playing = false;
    },

    clearTimer() {
      if (this.timer) {
        clearTimeout(this.timer);
        this.timer = 0;
      }
    },

    cancelAnim() {
      if (this.raf) {
        cancelAnimationFrame(this.raf);
        this.raf = 0;
      }
      this.anim = null;
    },

    /**
     * 停留进度 0→1，驱动中心卡的缓慢放大。
     * 位移期间冻结在 1：让出中心的那张要保持终点值，靠 zoomAt 的权重衰减回 1，
     * 否则会在位移开始的瞬间缩一下。拖拽期间归 0，避免卡片在 d=0 两侧来回跳。
     */
    holdProgress() {
      if (!this.breathe || this.reduceMotion || this.dragging) return 0;
      if (this.anim) return 1;
      const rest = this.interval - this.duration;
      if (rest <= 0) return 1;
      const t = (now() - this.lastStart - this.duration) / rest;
      return t <= 0 ? 0 : t >= 1 ? 1 : t;
    },

    /** 除了位移，缩放没走完时也要继续出帧 */
    needsFrame() {
      if (this.anim) return true;
      if (!this.breathe || this.reduceMotion || this.dragging) return false;
      if (!this.visible || document.hidden) return false;
      return this.holdProgress() < 1;
    },

    requestFrame() {
      if (!this.raf) this.raf = requestAnimationFrame(this.loop);
    },

    /** 当前的目标槽位：位移中取终点，否则取当前位置 */
    targetPos() {
      return this.anim ? this.anim.to : this.position;
    },

    /**
     * 前后翻 delta 张。
     * 基准取「目标槽位」而不是当前位置，否则在位移途中再次触发就会
     * 把上一段没走完的小数留在 position 里，从此再也回不到整数槽位。
     */
    step(delta) {
      if (!this.canRun) return;
      const base = Math.round(this.targetPos());
      this.cancelAnim();
      this.move(base + delta - this.position, this.duration, this.schedule);
    },

    /** 相对当前位置移动 delta 张（delta 需保证终点落在整数槽位） */
    move(delta, duration, done) {
      if (!this.canRun || !delta) {
        if (done) done();
        return;
      }
      this.clearTimer();
      const total = this.renderItems.length;
      const from = this.position;
      const to = from + delta;
      const ms = this.reduceMotion ? 0 : Math.max(0, duration);
      if (!ms) {
        this.position = norm(to, total);
        this.lastStart = now();
        this.render();
        if (done) done();
        if (this.needsFrame()) this.requestFrame();
        return;
      }
      const t0 = now();
      this.lastStart = t0;
      this.anim = { from, to, t0, ms, done, total };
      this.requestFrame();
    },

    loop() {
      this.raf = 0;
      const a = this.anim;
      if (a) {
        const t = Math.min(1, (now() - a.t0) / a.ms);
        this.position = a.from + (a.to - a.from) * this.ease(t);
        if (t >= 1) {
          this.position = norm(a.to, a.total);
          this.anim = null;
          this.render();
          if (a.done) a.done();
          if (this.needsFrame()) this.requestFrame();
          return;
        }
        this.render();
        this.requestFrame();
        return;
      }
      this.render();
      if (this.needsFrame()) this.requestFrame();
    },

    /** 切到第 index 张（按环形最短路径） */
    goTo(index, duration) {
      const n = this.items.length;
      if (!n || !this.canRun) return;
      const total = this.renderItems.length;
      const base = Math.round(this.targetPos());
      // 数量不足时 renderItems 里同一张图有多个副本，取离当前位置最近的那个，
      // 否则从第 5 张点回第 0 张会绕远路
      let delta = null;
      for (let k = index; k < total; k += n) {
        const d = wrap(k - base, total);
        if (delta === null || Math.abs(d) < Math.abs(delta)) delta = d;
      }
      this.cancelAnim();
      this.move(
        base + delta - this.position,
        duration === undefined ? this.duration : duration,
        this.schedule
      );
    },

    next() {
      this.step(1);
    },

    prev() {
      this.step(-1);
    },

    onCardClick(i, item, ev) {
      // 拖拽刚结束时补发的 click 不算数（触摸端会延迟到 touchend 之后才派发）
      if (this.dragging || now() - this.dragEndAt < CLICK_GUARD) return;
      const total = this.renderItems.length;
      const d = total > 1 ? wrap(i - Math.round(this.targetPos()), total) : 0;
      if (d === 0) {
        this.$emit("select", item, this.activeIndex, ev);
      } else {
        const base = Math.round(this.targetPos());
        this.cancelAnim();
        this.move(base + d - this.position, this.duration, this.schedule);
      }
    },

    /* ---------- 拖拽 ---------- */

    bindDrag() {
      if (!this.interactive) return;
      if (this.dragBound) return;
      this.dragBound = true;
      // 捕获阶段吃掉拖拽尾随的那个 click：桌面端 mouseup 之后浏览器还会补一发，
      // 不拦住会一路冒泡到宿主页面（落地页整页都绑了点击行为）
      this.$el.addEventListener("click", this.onClickCapture, true);
      this.$el.addEventListener("mousedown", this.onDragStart);
      this.$el.addEventListener("touchstart", this.onDragStart, {
        passive: true
      });
    },

    unbindDrag() {
      this.dragBound = false;
      if (this.$el) {
        this.$el.removeEventListener("click", this.onClickCapture, true);
        this.$el.removeEventListener("mousedown", this.onDragStart);
        this.$el.removeEventListener("touchstart", this.onDragStart);
      }
      this.detachDragMove();
    },

    attachDragMove(touch) {
      if (touch) {
        document.addEventListener("touchmove", this.onDragMove, {
          passive: false
        });
        document.addEventListener("touchend", this.onDragEnd);
        document.addEventListener("touchcancel", this.onDragEnd);
      } else {
        document.addEventListener("mousemove", this.onDragMove);
        document.addEventListener("mouseup", this.onDragEnd);
        // 在窗口外松手 / 窗口失焦时也要收尾，否则拖拽状态会一直挂着
        document.addEventListener("mouseleave", this.onDragEnd);
        window.addEventListener("blur", this.onDragEnd);
      }
    },

    detachDragMove() {
      document.removeEventListener("touchmove", this.onDragMove);
      document.removeEventListener("touchend", this.onDragEnd);
      document.removeEventListener("touchcancel", this.onDragEnd);
      document.removeEventListener("mousemove", this.onDragMove);
      document.removeEventListener("mouseup", this.onDragEnd);
      document.removeEventListener("mouseleave", this.onDragEnd);
      window.removeEventListener("blur", this.onDragEnd);
    },

    onClickCapture(ev) {
      if (this.dragging || now() - this.dragEndAt < CLICK_GUARD) {
        ev.stopPropagation();
        ev.preventDefault();
      }
    },

    onDragStart(ev) {
      if (!this.draggable || !this.canRun) return;
      if (ev.type === "mousedown" && ev.button !== 0) return;
      if (this.drag) return; // 已经在拖了（多指等），忽略后来的
      const p = point(ev);
      // 这里刻意不打断正在进行的位移：轻点、竖向滚动都会走到这里，
      // 一旦提前 cancelAnim 就会把卡片永久停在小数槽位上
      this.drag = {
        x: p.x,
        y: p.y,
        from: this.position,
        moved: false,
        axis: "",
        touch: ev.type === "touchstart"
      };
      this.attachDragMove(this.drag.touch);
    },

    onDragMove(ev) {
      const d = this.drag;
      if (!d) return;
      const p = point(ev);
      const dx = p.x - d.x;
      const dy = p.y - d.y;
      if (!d.axis) {
        if (Math.abs(dx) < DRAG_SLOP && Math.abs(dy) < DRAG_SLOP) return;
        if (Math.abs(dy) >= Math.abs(dx)) {
          // 竖向手势交还给页面滚动，动画不受影响
          this.drag = null;
          this.detachDragMove();
          return;
        }
        d.axis = "x";
        // 确认是横向拖拽，这时才接管动画，并以当前视觉位置为基准
        this.clearTimer();
        this.cancelAnim();
        d.from = this.position;
        d.x = p.x;
        this.dragging = true;
      }
      if (ev.cancelable) ev.preventDefault();
      d.moved = true;
      const step = DRAG_STEP * this.scale || 1;
      this.position = d.from - (p.x - d.x) / step;
      this.render();
    },

    onDragEnd() {
      const d = this.drag;
      this.drag = null;
      this.detachDragMove();
      if (!d) return;
      if (!d.moved) {
        this.schedule();
        return;
      }
      this.dragging = false;
      this.dragEndAt = now();
      const target = Math.round(this.position);
      this.move(
        target - this.position,
        Math.round(this.duration * 0.6),
        this.schedule
      );
    }
  }
};

/* ---------- 纯函数 ---------- */

function matches(query) {
  return !!(window.matchMedia && window.matchMedia(query).matches);
}

/** 统一取鼠标 / 触摸事件的坐标 */
function point(ev) {
  let t = ev;
  if (ev.touches && ev.touches.length) t = ev.touches[0];
  else if (ev.changedTouches && ev.changedTouches.length)
    t = ev.changedTouches[0];
  return { x: t.clientX || 0, y: t.clientY || 0 };
}

function normalizeItem(item) {
  if (typeof item === "string") return { src: item, alt: "" };
  return Object.assign({ alt: "" }, item || {});
}

/** 把差值折到 [-n/2, n/2)，即环形最短路径 */
function wrap(d, n) {
  return d - n * Math.round(d / n);
}

function norm(v, n) {
  return ((v % n) + n) % n;
}

function now() {
  return typeof performance !== "undefined" && performance.now
    ? performance.now()
    : Date.now();
}

function mixRgb(a, b, t) {
  const r = Math.round(a[0] + (b[0] - a[0]) * t);
  const g = Math.round(a[1] + (b[1] - a[1]) * t);
  const bl = Math.round(a[2] + (b[2] - a[2]) * t);
  return "rgb(" + r + ", " + g + ", " + bl + ")";
}
</script>

<style lang="scss" scoped>
/* 注意：类名前缀 fc- 已加入 vue.config.js 的 px2rem selectorBlackList，
   这里的 px 会原样保留。换到别的工程复用时请照做，否则 px 被转成 rem
   会跟着根字号漂移，和 JS 写的行内 px 对不上。 */
.fc-root {
  position: relative;
  width: 100%;
  overflow: hidden;
  outline: none;
  user-select: none;
  -webkit-user-select: none;
  -webkit-tap-highlight-color: transparent;
  /* 竖向滚动交给浏览器，横向留给自己处理 */
  touch-action: pan-y;

  &.is-dragging,
  &.is-dragging .fc-card {
    cursor: grabbing;
  }

  /* 纯展示：整层不吃指针事件，交互交还给底下的页面 */
  &.is-static,
  &.is-static .fc-card,
  &.is-static .fc-dot {
    pointer-events: none;
    cursor: default;
  }
}

.fc-viewport {
  position: absolute;
  left: 50%;
  top: 0;
  /* 以顶边中点为缩放原点，配合 margin-left:-600px 让舞台始终水平居中 */
  transform-origin: 50% 0;
}

.fc-stage {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  /* 刻意保持 transform-style: flat：卡片互不相交，用 z-index 排序比让
     浏览器做 3D 深度排序更稳定，各内核表现一致 */
}

.fc-card {
  position: absolute;
  box-sizing: border-box;
  overflow: hidden;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  box-shadow: 0 16px 44px rgba(0, 0, 0, 0.2);
  cursor: pointer;

  &--framed {
    border: 2px solid #c3a475;
    background: #0b0d0b;
  }
}

.fc-card__img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: fill;
  pointer-events: none;
}

.fc-card__meta {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 24px 28px 26px;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0) 0%,
    rgba(0, 0, 0, 0.85) 60%
  );
  pointer-events: none;
}

.fc-card__title {
  margin: 0;
  font-size: 34px;
  font-weight: 600;
  line-height: 1.2;
  color: #fff;
}

.fc-card__subtitle {
  margin: 8px 0 0;
  font-size: 20px;
  line-height: 1.3;
  color: rgba(255, 255, 255, 0.75);
}

.fc-dots {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.fc-dot {
  position: absolute;
  width: 8px;
  padding: 0;
  border: 0;
  background: #cfcfcf;
  transform: translateX(-50%);
  pointer-events: auto;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;

  /* 指示点本身只有 8px 高，且会跟着舞台一起缩小，单独撑出一块点按区域 */
  &::after {
    content: "";
    position: absolute;
    left: 50%;
    top: 50%;
    width: 44px;
    height: 44px;
    min-width: 100%;
    transform: translate(-50%, -50%);
  }

  /* 全局 reset 里有 html *{outline:0}，只能用 box-shadow 做焦点圈；
     :focus-visible 不支持的老内核走 :focus 兜底 */
  &:focus {
    box-shadow: 0 0 0 3px rgba(200, 164, 108, 0.65);
  }
}

.fc-live {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
}
</style>
